import { Key } from "react";
import _ from "lodash";
import qs from "qs";
import { z } from "zod/v4";

import { convertSignalArrayToSignalValue } from "components/form_components/SignalSelect";
import { FrameworkFilterOptions, RecordFilters } from "components/record_search/types";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import {
  ColumnSetting,
  ColumnSettingFieldEnum,
  DateSetting,
  RecordDto,
  RecordSearchRequestDto,
  RecordSearchRequestDtoFrameworksFrameworkRelationshipEnum as FrameworkRelationship,
  RecordSearchRequestDtoProcurementStageQualificationsEnum as QualificationsEnum,
  ViewConfiguration,
  ViewConfigurationFilterSettings,
  ViewConfigurationFilterSettingsRelevanceScoreEnum as RelevanceScoreEnum,
  ViewConfigurationFilterSettingsSuppliersMentionTypeEnum,
  ViewConfigurationSortOrder,
  ViewConfigurationTableSettings,
} from "lib/generated/app-api";
import { NoticesDto } from "lib/hooks/api/notices/useNotices";
import { encodeCompressed, parseBooleanValue } from "lib/hooks/useURLState";
import { routes } from "lib/routes";
import { SortState } from "lib/search/types";
import {
  ALL_COMPETITORS_TOKEN,
  ALL_PARTNERS_TOKEN,
  CountryRegionSchema,
  getSignalTypes,
  Status,
  StatusValues,
  TenderStage,
} from "lib/types/models";
import { getMinRelevanceScore, mapSignalsToViewSignals } from "lib/utils/signalUtils";
import { NoticesSearchRequest } from "../../lib/generated/app-service-gql/graphql";
import {
  FrameworkActivity,
  NoticesQualifications as NoticeQualification,
  NoticesQualifications,
  NoticesSort as SortEnum,
  NoticesSortOrder as SortOrder,
  NoticesStages as Stages,
  NoticesStagesFilter,
  NoticesStagesv2,
  SearchRequestQualifications as SearchReqQualification,
  SupplierMentionType,
} from "../../lib/types/graphQLEnums";

const DateFilterSchema = z.object({
  from: z.string().optional(),
  relativeFrom: z.string().optional(),
  to: z.string().optional(),
  relativeTo: z.string().optional(),
});
type DateFilter = z.infer<typeof DateFilterSchema>;

export const NoticeFiltersSchema = z.object({
  procurementStageQualifications: z.array(z.enum(QualificationsEnum)),
  // TODO: Remove signals filter from UI
  signals: z.array(z.string()),
  assignee: z.array(z.string()),
  suppliers: z.array(z.number()),
  supplierGuids: z.array(z.string()).optional(),
  supplierSme: z.union([z.boolean(), z.string()]),
  buyerListIds: z.array(z.string()),
  buyers: z.array(z.string()),
  // This is only required for compatibility with report pages.
  // The buyer ids will be converted to buyer guids before page loads,
  // so we don't have to support this in the UI.
  // @deprecated - use GUIDS in buyers filter.
  buyerIds: z.array(z.coerce.number()).optional(),
  buyerCategories: z.array(z.string()),
  buyerCountryRegions: CountryRegionSchema,
  text: z.string(),
  value: z.object({
    from: z.coerce.number().optional(),
    to: z.coerce.number().optional(),
    hideNulls: z.coerce.boolean().optional(),
  }),
  stage: z.array(z.union([z.enum(StatusValues), z.enum(NoticesStagesv2)])),
  relevanceScore: z.array(z.enum(RelevanceScoreEnum)),
  awardDate: z.object({
    filter: DateFilterSchema.optional(),
    hideNulls: z.coerce.boolean().optional(),
  }),
  closeDate: z.object({
    filter: DateFilterSchema.optional(),
    hideNulls: z.coerce.boolean().optional(),
  }),
  expiryDate: z.object({
    filter: DateFilterSchema.optional(),
    hideNulls: z.coerce.boolean().optional(),
  }),
  publishDate: DateFilterSchema,
  noticeLists: z.array(z.string()).optional(),
  frameworkIds: z.array(z.string()),
  supplierMentionType: z.enum(SupplierMentionType).optional(),
  frameworkFilterOptions: z.union([z.string(), z.enum(FrameworkFilterOptions)]),
  keywords: z.array(z.string()),
  excludeKeywords: z.array(z.string()),
  cpvCodes: z.array(z.string()),
  includeHiddenNotices: z.coerce.boolean().optional(),
  noticeIds: z.array(z.string()).optional(),
  sort: z.object({
    field: z.string(),
    order: z.enum(["ASC", "DESC"]),
  }),
});
export type NoticeFilters = z.infer<typeof NoticeFiltersSchema>;

// Some props here are assigned undefined explicitly to ensure correct amplitude tracking.
// This fixes the deep equality check between previous and next filter objects and captures only the filter field being applied.
export const DEFAULT_FILTERS: NoticeFilters = {
  procurementStageQualifications: [
    "unqualified",
    "in_review",
    "bid_prep",
    "bid_submitted",
    "pre_engage_to_do",
    "pre_engage_done",
  ],
  signals: ["Keywords", "Partners", "Competitors", "CPV codes"],
  assignee: [],
  buyerListIds: [],
  buyers: [],
  buyerCategories: [],
  buyerCountryRegions: {
    countries: [],
    regions: [],
    selected: [],
  },
  value: { to: undefined, from: undefined, hideNulls: undefined },
  text: "",
  stage: [],
  relevanceScore: [],
  awardDate: {
    filter: { from: undefined, relativeFrom: undefined, to: undefined, relativeTo: undefined },
    hideNulls: undefined,
  },
  closeDate: {
    filter: { from: undefined, relativeFrom: undefined, to: undefined, relativeTo: undefined },
    hideNulls: undefined,
  },
  expiryDate: {
    filter: { from: undefined, relativeFrom: undefined, to: undefined, relativeTo: undefined },
    hideNulls: undefined,
  },
  publishDate: {},
  sort: {
    field: "relevance_score",
    order: "DESC",
  },
  suppliers: [],
  supplierGuids: [],
  supplierSme: "",
  frameworkIds: [],
  frameworkFilterOptions: "",
  supplierMentionType: undefined,
  keywords: [],
  excludeKeywords: [],
  cpvCodes: [],
};

export const EMPTY_FILTERS: NoticeFilters = {
  ...DEFAULT_FILTERS,
  closeDate: {},
  expiryDate: {},
  procurementStageQualifications: [],
  signals: [],
  value: {},
};

// In the rare case that sort order is not defined for a view, this is the failsafe
export const DEFAULT_NOTICE_SORT_ORDER: SortState = {
  field: "publish_date",
  order: "DESC",
};

export type FilterSection = "buyers" | "suppliers" | "frameworks";

type Pagination = {
  pageSize: number;
  current: number;
};

// map between rails enums to GQL enum
const qualificationMap = new Map<QualificationsEnum, SearchReqQualification>([
  [QualificationsEnum.NotRelevant, SearchReqQualification.NotRelevant],
  [QualificationsEnum.InReview, SearchReqQualification.InReview],
  [QualificationsEnum.BidPrep, SearchReqQualification.BidPrep],
  [QualificationsEnum.BidSubmitted, SearchReqQualification.BidSubmitted],
  [QualificationsEnum.NoBid, SearchReqQualification.NoBid],
  [QualificationsEnum.Won, SearchReqQualification.Won],
  [QualificationsEnum.Lost, SearchReqQualification.Lost],
  [QualificationsEnum.PreEngageToDo, SearchReqQualification.PreEngageToDo],
  [QualificationsEnum.PreEngageDone, SearchReqQualification.PreEngageDone],
  [QualificationsEnum.PreEngageNotRelevant, SearchReqQualification.PreEngageNotRelevant],
  [QualificationsEnum.Unqualified, SearchReqQualification.Unqualified],
]);

function formatDateToView(date: {
  filter?: DateFilter;
  hideNulls?: boolean;
}): DateSetting | undefined {
  const { filter } = date;
  if (
    _.isEmpty(date) ||
    (!filter?.from && !filter?.to && !filter?.relativeFrom && !filter?.relativeTo) ||
    !date
  ) {
    return undefined;
  } else {
    return {
      from: filter.from ? new Date(filter.from) : undefined,
      to: filter.to ? new Date(filter.to) : undefined,
      relativeFrom: filter.relativeFrom,
      relativeTo: filter.relativeTo,
    };
  }
}

export function convertNoticeFiltersToView(
  noticeFilters: NoticeFilters,
  columns?: ColumnSetting[],
): ViewConfiguration {
  if (noticeFilters.includeHiddenNotices) {
    noticeFilters.procurementStageQualifications = Object.values(QualificationsEnum);
  } else {
    noticeFilters.procurementStageQualifications = [];
  }

  const filterSettings: ViewConfigurationFilterSettings = {
    text: noticeFilters.text ? noticeFilters.text : undefined,
    stage: noticeFilters.stage.length > 0 ? noticeFilters.stage : undefined,
    buyerIds: undefined,
    buyerGuids: noticeFilters.buyers,
    closeDate: formatDateToView(noticeFilters.closeDate),
    closeNulls:
      noticeFilters.closeDate?.hideNulls !== undefined
        ? !noticeFilters.closeDate?.hideNulls
        : undefined,
    expiryDate: formatDateToView(noticeFilters.expiryDate),
    expiryNulls:
      noticeFilters.expiryDate?.hideNulls !== undefined
        ? !noticeFilters.expiryDate?.hideNulls
        : undefined,
    publishDate: formatDateToView({ filter: noticeFilters.publishDate }),
    userTagIds: [],
    buyerCountryCodes: noticeFilters.buyerCountryRegions.countries,
    buyerRegionIds: noticeFilters.buyerCountryRegions.regions,
    indicatorTagIds: [],
    buyerCategoryId: noticeFilters.buyerCategories,
    buyerCategoryNulls: false,
    value: !_.isEmpty(noticeFilters.value)
      ? {
          from: noticeFilters.value?.from,
          to: noticeFilters.value?.to,
          nulls: undefined,
        }
      : undefined,
    valueNulls:
      noticeFilters.value?.hideNulls !== undefined ? !noticeFilters.value?.hideNulls : undefined,
    showUserHiddenLeads: "1",
    qualificationStates: undefined,
    includeAllDisqualifiedStates: false,
    assignee: noticeFilters.assignee.length > 0 ? noticeFilters.assignee : undefined,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    suppliers:
      _.isEmpty(noticeFilters.suppliers) &&
      _.isEmpty(noticeFilters.supplierGuids) &&
      noticeFilters.supplierSme === ""
        ? undefined
        : {
            id: noticeFilters.suppliers,
            guid: noticeFilters.supplierGuids,
            isSme: noticeFilters.supplierSme === "" ? null : noticeFilters.supplierSme,
            mentionType:
              noticeFilters.supplierMentionType &&
              (noticeFilters.supplierMentionType as ViewConfigurationFilterSettingsSuppliersMentionTypeEnum),
          },
    procurementStageQualifications:
      noticeFilters.procurementStageQualifications.length > 0
        ? noticeFilters.procurementStageQualifications
        : undefined,
    relevanceScore: noticeFilters.relevanceScore,
    buyerListIds: noticeFilters.buyerListIds.length > 0 ? noticeFilters.buyerListIds : undefined,
    signalCategory: undefined,
    signals: !_.isEmpty(noticeFilters.signals)
      ? mapSignalsToViewSignals(noticeFilters.signals)
      : { categories: [], ids: [] },
    noticeLists:
      noticeFilters.noticeLists && noticeFilters.noticeLists.length > 0
        ? noticeFilters.noticeLists.map((noticeList) => Number(noticeList))
        : undefined,
    keywords: noticeFilters.keywords.length > 0 ? noticeFilters.keywords : undefined,
    excludeKeywords:
      noticeFilters.excludeKeywords.length > 0 ? noticeFilters.excludeKeywords : undefined,
    cpvCodes: noticeFilters.cpvCodes.length > 0 ? noticeFilters.cpvCodes : undefined,
    frameworks: {
      ids: noticeFilters.frameworkIds,
      frameworkRelationship: convertFrameworkRelationshipFilter(
        noticeFilters.frameworkFilterOptions,
      ),
    },
  };

  const sortOrder: ViewConfigurationSortOrder = {
    field: noticeFilters.sort.field,
    order: noticeFilters.sort.order === "ASC" ? "ASC" : "DESC",
  };

  const tableSettings: ViewConfigurationTableSettings = {
    columns: columns || DEFAULT_NOTICE_COLUMNS,
  };

  return {
    filterSettings,
    sortOrder,
    tableSettings,
  };
}

export function convertNoticeFiltersToApi(
  filters: NoticeFilters,
  pagination?: Pagination,
  isPreview = false,
): RecordSearchRequestDto {
  if (filters.buyerCategories) {
    // handles when the parent category is selected as it is stringified array
    filters.buyerCategories = filters.buyerCategories.reduce<string[]>((acc, val) => {
      val.split(",").map((v) => acc.push(v));
      return acc;
    }, []);
  }

  const recordListIds = filters.noticeLists
    ? filters.noticeLists.map((noticeList) => Number(noticeList))
    : undefined;

  const data: RecordSearchRequestDto = {
    anySignals: convertSignalArrayToSignalValue(filters.signals),
    procurementStageQualifications: filters.procurementStageQualifications,
    assignee: filters.assignee,
    buyerListIds: filters.buyerListIds,
    buyers: {
      guid: filters.buyers,
      categories: filters.buyerCategories.length > 0 ? { id: filters.buyerCategories } : undefined,
      regionIds: filters.buyerCountryRegions.regions,
      countryCode: filters.buyerCountryRegions.countries,
    },
    suppliers: {
      id: filters.suppliers,
      isSme: parseBooleanValue(filters.supplierSme),
      guid: filters.supplierGuids?.filter(
        (guid) => ![ALL_COMPETITORS_TOKEN, ALL_PARTNERS_TOKEN].includes(guid),
      ),
    },
    relevanceScore: filters.relevanceScore,
    text: filters.text,
    value: {
      from: filters.value?.from,
      to: filters.value?.to,
      includeNulls: !filters.value?.hideNulls,
    },
    stage: filters.stage,
    closeDate: {
      filter: {
        ...filters.closeDate?.filter,
        from: filters.closeDate?.filter?.from
          ? new Date(filters.closeDate?.filter?.from)
          : undefined,
        to: filters.closeDate.filter?.to ? new Date(filters.closeDate.filter?.to) : undefined,
      },
      includeNulls: !filters.closeDate?.hideNulls,
    },
    expiryDate: {
      filter: {
        ...filters.expiryDate?.filter,
        from: filters.expiryDate?.filter?.from
          ? new Date(filters.expiryDate?.filter?.from)
          : undefined,
        to: filters.expiryDate.filter?.to ? new Date(filters.expiryDate.filter?.to) : undefined,
      },
      includeNulls: !filters.expiryDate?.hideNulls,
    },
    publishDate: {
      ...filters.publishDate,
      from: filters.publishDate.from ? new Date(filters.publishDate.from) : undefined,
      to: filters.publishDate.to ? new Date(filters.publishDate.to) : undefined,
    },
    limit: pagination ? pagination.pageSize : undefined,
    offset: pagination ? (pagination.current - 1) * pagination.pageSize : undefined,
    sort:
      filters.sort?.field === "signals"
        ? DEFAULT_NOTICE_SORT_ORDER.field
        : filters.sort?.field || DEFAULT_NOTICE_SORT_ORDER.field,
    recordListIds: recordListIds,
    sortOrder: filters.sort?.order || DEFAULT_NOTICE_SORT_ORDER.order,
    signalSort: filters.sort?.field === "signals" ? filters.sort?.order : undefined,
    preview: isPreview,
    frameworks: {
      ids: filters.frameworkIds,
      frameworkRelationship: convertFrameworkRelationshipFilter(filters.frameworkFilterOptions),
    },
  };

  return data;
}

function convertOldQualifcationFiltersForOpporunties(includeHiddenNotices: boolean) {
  const allStates = [...qualificationMap.values()];
  const hiddenStates = [
    SearchReqQualification.NotRelevant,
    SearchReqQualification.PreEngageNotRelevant,
  ];
  return includeHiddenNotices
    ? allStates
    : allStates.filter((state) => !hiddenStates.includes(state));
}

export function convertNoticeFiltersToSearchRequest(
  filters: Partial<NoticeFilters>,
  pagination?: Pagination,
): NoticesSearchRequest {
  if (filters.buyerCategories) {
    filters.buyerCategories = filters.buyerCategories.reduce<string[]>((acc, val) => {
      val.split(",").map((v) => acc.push(v));
      return acc;
    }, []);
  }

  const sortFields = mapToSortOrder(filters.sort ?? DEFAULT_NOTICE_SORT_ORDER);
  const signals = convertSignalArrayToSignalValue(filters.signals);

  const qualifications = convertOldQualifcationFiltersForOpporunties(
    filters.includeHiddenNotices || false,
  );

  const data: NoticesSearchRequest = {
    signals: {
      // TODO: fix this
      categories: signals.categories,
      signalIds: signals.ids,
    },
    buyerFilters: {
      buyers: filters.buyers,
      buyerCategories: filters.buyerCategories,
      buyerCountryCodes: filters.buyerCountryRegions?.countries,
      buyerLists: filters.buyerListIds,
      buyerRegions: filters.buyerCountryRegions?.regions,
    },
    cpvCodes: filters.cpvCodes,
    suppliers: filters.supplierGuids,
    isSME: getSupplierIsSME(filters.supplierSme),
    searchText: filters.text,
    value: {
      from: filters.value?.from,
      to: filters.value?.to,
      hideNulls: filters.value?.hideNulls,
    },
    stages: mapToStages(filters.stage ?? []),
    awardDate: {
      filter: {
        ...filters.awardDate?.filter,
        from: filters.awardDate?.filter?.from,
        to: filters.awardDate?.filter?.to,
      },
      hideNulls: filters.awardDate?.hideNulls,
    },
    closeDate: {
      filter: {
        ...filters.closeDate?.filter,
        from: filters.closeDate?.filter?.from,
        to: filters.closeDate?.filter?.to,
      },
      hideNulls: filters.closeDate?.hideNulls,
    },
    expiryDate: {
      filter: {
        ...filters.expiryDate?.filter,
        from: filters.expiryDate?.filter?.from,
        to: filters.expiryDate?.filter?.to,
      },
      hideNulls: filters.expiryDate?.hideNulls,
    },
    publishDate: {
      filter: {
        from: filters.publishDate?.from,
        to: filters.publishDate?.to,
        relativeFrom: filters.publishDate?.relativeFrom,
        relativeTo: filters.publishDate?.relativeTo,
      },
    },
    supplierMentionType: Object.values(SupplierMentionType).includes(
      filters.supplierMentionType as SupplierMentionType,
    )
      ? (filters.supplierMentionType as SupplierMentionType)
      : SupplierMentionType.MentionedOrDirectlyAwarded,
    qualifications,
    assignees: filters.assignee,
    minScore: getMinRelevanceScore(filters.relevanceScore ?? []),
    keywords: filters.keywords,
    excludeKeywords: filters.excludeKeywords,
    frameworks: filters.frameworkIds,
    frameworkActivity: Object.values(FrameworkActivity).includes(
      filters.frameworkFilterOptions as FrameworkActivity,
    )
      ? (filters.frameworkFilterOptions as FrameworkActivity)
      : undefined,
    noticeLists:
      filters.noticeLists && filters.noticeLists.length > 0
        ? filters.noticeLists.map((listID) => Number(listID))
        : undefined,
    noticeIds: filters.noticeIds,
    limit: pagination?.pageSize ?? 10,
    page: pagination?.current ?? 1,
    sort: sortFields.field,
    sortOrder: sortFields.order,
  };

  return data;
}

function mapToStages(stages: (Status | NoticesStagesv2)[]): NoticesStagesFilter[] {
  const getStage = (stage: Status | NoticesStagesv2) => {
    switch (stage) {
      // Old
      case "OPEN":
        return NoticesStagesFilter.Open;
      case "STALE_PRE_TENDER":
        return NoticesStagesFilter.StalePreTender;
      case "PRE_TENDER":
        return NoticesStagesFilter.PreTender;
      case "CLOSED":
        return NoticesStagesFilter.Closed;
      case "AWARDED":
        return NoticesStagesFilter.Awarded;
      // New
      case NoticesStagesv2.Pipeline:
        return NoticesStagesFilter.Pipeline;
      case NoticesStagesv2.PreTenderV2:
        return NoticesStagesFilter.PreTenderV2;
      case NoticesStagesv2.StalePreTenderV2:
        return NoticesStagesFilter.StalePreTenderV2;
      case NoticesStagesv2.OpenTender:
        return NoticesStagesFilter.OpenTender;
      case NoticesStagesv2.ClosedTender:
        return NoticesStagesFilter.ClosedTender;
      case NoticesStagesv2.CancelledTender:
        return NoticesStagesFilter.CancelledTender;
      case NoticesStagesv2.UnknownTender:
        return NoticesStagesFilter.UnknownTender;
      case NoticesStagesv2.AwardedContract:
        return NoticesStagesFilter.AwardedContract;
      case NoticesStagesv2.ExpiredContract:
        return NoticesStagesFilter.ExpiredContract;
      case NoticesStagesv2.CancelledAward:
        return NoticesStagesFilter.CancelledAward;
      default:
        throw new Error(`Unknown stage: ${stage}`);
    }
  };
  return stages.map((stage) => getStage(stage));
}

export function getNoticeDateValue(notice: {
  stagev2?: NoticesStagesv2 | null;
  expiryDate?: string | null;
  closeDate?: string | null;
  publishDate?: string | null;
}) {
  switch (notice.stagev2) {
    case NoticesStagesv2.AwardedContract:
    case NoticesStagesv2.ExpiredContract:
    case NoticesStagesv2.CancelledAward:
      return notice.expiryDate;
    case NoticesStagesv2.OpenTender:
    case NoticesStagesv2.ClosedTender:
    case NoticesStagesv2.CancelledTender:
    case NoticesStagesv2.UnknownTender:
      return notice.closeDate;
    default:
      return notice.publishDate;
  }
}

export function getFrontendQualification(qualification: NoticeQualification) {
  const res = Array.from(qualificationMap.entries()).find(
    ([_, value]) => value.toString() === qualification.toString(),
  )?.[0];

  if (!res) {
    throw new Error(`Unknown qualification: ${qualification}`);
  }

  return res;
}

export function getGQLSearchQualificationType(qualification: QualificationsEnum) {
  const res = Array.from(qualificationMap.entries()).find(
    ([key, _]) => key.toString() === qualification.toString(),
  )?.[1];

  if (!res) {
    throw new Error(`Unknown qualification: ${qualification}`);
  }

  return res;
}

export function getGQLQualificationType(qualification: QualificationsEnum): NoticesQualifications {
  switch (qualification) {
    case QualificationsEnum.BidPrep:
      return NoticesQualifications.BidPrep;
    case QualificationsEnum.BidSubmitted:
      return NoticesQualifications.BidSubmitted;
    case QualificationsEnum.InReview:
      return NoticesQualifications.InReview;
    case QualificationsEnum.Lost:
      return NoticesQualifications.Lost;
    case QualificationsEnum.NoBid:
      return NoticesQualifications.NoBid;
    case QualificationsEnum.NotRelevant:
      return NoticesQualifications.NotRelevant;
    case QualificationsEnum.PreEngageDone:
      return NoticesQualifications.PreEngageDone;
    case QualificationsEnum.PreEngageNotRelevant:
      return NoticesQualifications.PreEngageNotRelevant;
    case QualificationsEnum.PreEngageToDo:
      return NoticesQualifications.PreEngageToDo;
    case QualificationsEnum.Won:
      return NoticesQualifications.Won;
    case QualificationsEnum.Unqualified:
      return NoticesQualifications.Unqualified;
    default:
      throw new Error(`Unknown qualification: ${qualification}`);
  }
}

/**
 * All possible sort fields for notices mapped to the GQL enum. All sort fields that have been used for saved views are accounted for here.
 * Worst case secanrio we just sort by relevance_score
 * @param sort
 * @returns
 */
function mapToSortOrder(sort: SortState) {
  //TODO: limit use of the generated types due to automatic renaming
  const order = sort.order === "ASC" ? SortOrder.Asc : SortOrder.Desc;
  const getField = (sortField: Key | undefined) => {
    switch (sortField) {
      case "award_date":
        return SortEnum.AwardDate;
      case "publish_date":
      case "date_added":
        return SortEnum.PublishedDate;
      case "close_date":
        return SortEnum.CloseDate;
      case "expiry_date":
        return SortEnum.ExpiryDate;
      case "value":
        return SortEnum.ValueAmount;
      case "title":
        return SortEnum.Title;
      case "country":
        return SortEnum.Country;
      case "relevance_score":
      default:
        return SortEnum.Score;
    }
  };

  return { field: getField(sort.field), order: order };
}

export function convertRecordFiltersToNoticeFilters(filters: RecordFilters): NoticeFilters {
  let stage: NoticeFilters["stage"] =
    (filters.stage?.map((v) => v.toUpperCase()) as NoticeFilters["stage"]) || [];

  if (filters.searchMode === "AWARDS") {
    stage = ["AWARDED"];
  }
  if (filters.searchMode === "TENDERS") {
    stage = ["CLOSED", "OPEN", "PRE_TENDER", "STALE_PRE_TENDER"];
  }

  return {
    assignee: filters.assignee || [],
    buyerCategories: filters.buyerCategoryIds || [],
    buyerListIds: filters.buyerListIds || [],
    buyers: filters.buyerGuids || [],
    buyerIds: filters.buyerIds,
    buyerCountryRegions: {
      countries: filters.buyerCountry || [],
      regions: filters.buyerRegions || [],
    },
    awardDate: {
      // Believe this is old code and will be removed in layer cake cleanup so not investing time into getting it working correctly
      // filter: {
      //   ...filters.awardDate?.filter,
      //   to: filters.awardDate?.filter?.to?.toISOString(),
      //   from: filters.awardDate?.filter?.from?.toISOString(),
      // },
      // hideNulls:
      //   filters.awardDate?.includeNulls === undefined ? false : !filters.awardDate?.includeNulls,
    },
    closeDate: {
      filter: {
        ...filters.closeDate?.filter,
        to: filters.closeDate?.filter?.to?.toISOString(),
        from: filters.closeDate?.filter?.from?.toISOString(),
      },
      hideNulls:
        filters.closeDate?.includeNulls === undefined ? false : !filters.closeDate?.includeNulls,
    },
    expiryDate: {
      filter: {
        ...filters.expiryDate?.filter,
        to: filters.expiryDate?.filter?.to?.toISOString(),
        from: filters.expiryDate?.filter?.from?.toISOString(),
      },
      hideNulls:
        filters.expiryDate?.includeNulls === undefined ? false : !filters.expiryDate?.includeNulls,
    },
    procurementStageQualifications: filters.procurementStageQualifications || [],
    publishDate: {
      from: filters.publishDate?.from?.toISOString(),
      to: filters.publishDate?.to?.toISOString(),
      relativeFrom: filters.publishDate?.relativeFrom,
      relativeTo: filters.publishDate?.relativeTo,
    },
    signals: filters.signalCategory || [],
    sort: {
      field: filters.sort || DEFAULT_NOTICE_SORT_ORDER.field,
      order: filters.sortOrder || DEFAULT_NOTICE_SORT_ORDER.order,
    },
    stage,
    relevanceScore: filters.relevanceScore || [],
    suppliers: filters.supplierIds || [],
    supplierSme: filters.supplierSme === undefined ? "" : filters.supplierSme,
    text: filters.text || "",
    value: {
      from: filters.value?.from,
      to: filters.value?.to,
      hideNulls: filters.value?.includeNulls === undefined ? false : !filters.value?.includeNulls,
    },
    noticeLists: filters.recordListIds?.map((recordListId) => recordListId.toString()) || [],
    frameworkIds: filters.frameworkIds || [],
    frameworkFilterOptions:
      filters.hideFrameworkAgreementType === undefined ? "" : filters.hideFrameworkAgreementType,
    supplierMentionType:
      filters.supplierMentionType || SupplierMentionType.MentionedOrDirectlyAwarded,
    keywords: filters.keywords || [],
    excludeKeywords: filters.excludeKeywords || [],
    cpvCodes: filters.cpvCodes || filters.cpvDimensions || [],
  };
}

export function generateNoticeUrl(noticeFilters: Partial<NoticeFilters>, prefix = "") {
  const params = {
    noticeFilters,
  };
  const paramsString = qs.stringify(params, { arrayFormat: "brackets" });
  return `${prefix}?${paramsString}`;
}

export function generateNoticeSearchUrl(
  noticeFilters: Partial<NoticeFilters>,
  selectedViewId?: string,
  openFilterDrawer?: boolean,
) {
  const params = {
    current_view_id: selectedViewId,
    noticeFilters: encodeCompressed(noticeFilters),
    showNoView: selectedViewId === undefined,
    openFilterDrawer: !!openFilterDrawer,
  };
  const paramsString = qs.stringify(params, { arrayFormat: "brackets" });
  return `${routes.notices}?${paramsString}`;
}

export function generateNoticeAnalysisUrl(
  noticeFilters: Partial<NoticeFilters>,
  selectedViewId?: string,
  hasUserModifiedView?: boolean,
) {
  const params = {
    current_view_id: selectedViewId,
    noticeFilters: hasUserModifiedView !== false ? encodeCompressed(noticeFilters) : undefined,
    showNoView: selectedViewId === undefined,
  };
  const paramsString = qs.stringify(params, { arrayFormat: "brackets", encodeValuesOnly: true });
  return `${routes.noticeAnalysis}?${paramsString}`;
}

/**
 * Gets record details url and appends active notice filter params to url
 * if they are actioned
 */
export function recordDetailsUrl(guid: string, filters?: NoticeFilters): string {
  if (filters) {
    // Only take the filters that are actually used in highlighting
    const { supplierMentionType, supplierGuids, keywords, text } = filters;

    const newFilters: NoticeFilters = {
      ...EMPTY_FILTERS,
      supplierMentionType,
      supplierGuids,
      keywords,
      text,
    };
    const compressedFilters = encodeCompressed(newFilters);

    const params = qs.stringify(
      { noticeFilters: compressedFilters },
      { arrayFormat: "brackets", addQueryPrefix: true, encodeValuesOnly: true },
    );
    return `${routes.records}/${guid}${params}`;
  }

  return `${routes.records}/${guid}`;
}

export function generateProcurementUrl(
  procurementProcessId: string,
  stage?: "preTender" | "tender" | "award" | "PreTender" | "Tender" | "Award",
): string {
  if (stage) {
    return `${routes.procurement}/${procurementProcessId}/${stage === "preTender" || stage === "PreTender" ? "pre_tender" : stage.toLowerCase()}`;
  }
  return `${routes.procurement}/${procurementProcessId}`;
}

function convertFrameworkRelationshipFilter(
  hideFrameworkAgreementType?: string,
): FrameworkRelationship[] {
  switch (hideFrameworkAgreementType) {
    case FrameworkFilterOptions.HIDE_FRAMEWORK_AGREEMENT_NOTICES:
      return [FrameworkRelationship.CallOff, FrameworkRelationship.NotFrameworkRelated];

    case FrameworkFilterOptions.HIDE_ALL_FRAMEWORK_ACTIVITY:
      return [FrameworkRelationship.NotFrameworkRelated];

    case FrameworkFilterOptions.SHOW_ONLY_FRAMEWORK_CALL_OFFS:
      return [FrameworkRelationship.CallOff];

    // TODO: If options are added later, this API can support filtering by "FRAMEWORK_NOTICE"
    default:
      return [];
  }
}

export function convertFrameworkRelationshipFilterToFrameworkFilterOptions(
  frameworkRelationship?: FrameworkRelationship[],
): FrameworkFilterOptions | string {
  if (!frameworkRelationship) {
    return "";
  }

  if (
    frameworkRelationship.includes(FrameworkRelationship.CallOff) &&
    frameworkRelationship.includes(FrameworkRelationship.NotFrameworkRelated)
  ) {
    return FrameworkFilterOptions.HIDE_FRAMEWORK_AGREEMENT_NOTICES;
  }

  if (frameworkRelationship.includes(FrameworkRelationship.CallOff)) {
    return FrameworkFilterOptions.SHOW_ONLY_FRAMEWORK_CALL_OFFS;
  }

  if (frameworkRelationship.includes(FrameworkRelationship.NotFrameworkRelated)) {
    return FrameworkFilterOptions.HIDE_ALL_FRAMEWORK_ACTIVITY;
  }

  return "";
}

export function getLotTitle(
  lotNumber: string | null | undefined,
  lotTitle: string | null | undefined,
): string | null {
  if (lotTitle) {
    // sometimes lot_title starts with "Lot 1 - ...", sometimes not
    if (lotTitle.match(/^Lot\s\d+/) || !lotNumber) {
      return lotTitle;
    }
    return `Lot ${lotNumber}: ${lotTitle}`;
  }
  if (lotNumber) {
    return `Lot ${lotNumber}`;
  }
  return null;
}

export function mapToTenderStage(stage: Stages) {
  switch (stage) {
    case Stages.Awarded:
      return TenderStage.AWARDED;
    case Stages.Closed:
      return TenderStage.CLOSED;
    case Stages.Open:
      return TenderStage.OPEN;
    case Stages.PreTender:
      return TenderStage.PRE_TENDER;
    case Stages.StalePreTender:
      return TenderStage.STALE_PRE_TENDER;
    default:
      throw new Error(`Unknown stage: ${stage}`);
  }
}
function getSupplierIsSME(supplierSme?: string | boolean) {
  if (typeof supplierSme === "boolean") {
    return supplierSme;
  }
  if (typeof supplierSme === "string") {
    if (supplierSme.toLowerCase() === "true") {
      return true;
    }

    if (supplierSme.toLowerCase() === "false") {
      return false;
    }
  }
  return undefined;
}

export function showSavedViewWarningBanner(
  selectedQualifications: QualificationsEnum[],
  selectedAssignees: string[],
) {
  const meetsQualificationCriteria =
    selectedQualifications.length > 0 && !selectedQualifications.includes("unqualified");

  const hasAssigneesApplied = !!selectedAssignees.length;

  return meetsQualificationCriteria || hasAssigneesApplied;
}

export const DEFAULT_NOTICE_COLUMNS: ColumnSetting[] = [
  { field: "relevance_score" },
  { field: "title" },
  { field: "buyer" },
  { field: "stage" },
  { field: "value" },
  { field: "qualification" },
  { field: "assignee" },
  { field: "published_date" },
];

export const ALL_NOTICE_COLUMNS: ColumnSetting[] = [
  { title: "Signal score", field: "relevance_score" },
  { title: "Signals", field: "indicator" },
  { title: "Title & buyer", field: "title", disabled: true },
  { title: "Buyer type", field: "buyer_categories" },
  { title: "Actions", field: "qualification" },
  { title: "Stage", field: "stage" },
  { title: "Value", field: "value" },
  { title: "Country", field: "country" },
  { title: "Publish date", field: "published_date" },
  { title: "Close date", field: "close_date" },
  { title: "Award date", field: "award_date" },
  { title: "Expiry date", field: "expiry_date" },
  { title: "Supplier(s)", field: "suppliers" },
  { title: "SME suppliers?", field: "is_sme" },
  { title: "Framework", field: "framework" },
];

export type RecordColumnTypeMap = {
  [key in ColumnSettingFieldEnum]: ColumnType<RecordDto>;
};

export const NoticeColumnsSchema = z.enum(ColumnSettingFieldEnum);
export type NoticeColumns = z.infer<typeof NoticeColumnsSchema>;

const columnCopies: Record<NoticeColumns, string> = ALL_NOTICE_COLUMNS.reduce(
  (acc, { title, field }) => {
    if (field === "published_date") {
      return { ...acc, publish_date: title };
    }
    return { ...acc, [field]: title };
  },
  {} as Record<NoticeColumns, string>,
);

export const getNoticeColumnCopy = (column?: string): string => {
  if (!column || !(column in columnCopies)) return "";
  return columnCopies[column as NoticeColumns];
};

export function mapNoticeForActions(r: NoticesDto) {
  return {
    id: r.guid,
    name: r.name || "",
    value: r.value || undefined,
    procurementStage: {
      id: r.procurementStage.id,
      stage: r.procurementStage.stage ? r.procurementStage.stage.toString() : "",
      qualification: r.qualification ? getFrontendQualification(r.qualification) : undefined,
    },
    stageV2: r.stagev2 || undefined,
    stage: r.stage ? r.stage.toString() : "",
    closeDate: r.closeDate || undefined,
    expiryDate: r.expiryDate || undefined,
    buyers: r.buyers.map((buyer) => buyer.guid),
    suppliers: [...new Set(r.suppliers.map((suppliers) => suppliers.guid))],
    score: r.score,
    signalTypes: getSignalTypes([]),
    opportunities: r.opportunities,
  };
}

export function getAppliedFilters(filters: NoticeFilters) {
  const appliedFilters = Object.entries(filters).filter(([key, value]) => {
    if (key === "procurementStageQualifications") {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (key === "buyerCountryRegions") {
      const val = value as {
        countries: string[];
        regions: string[];
      };

      if (val.countries?.length > 0 || val.regions?.length > 0) {
        return true;
      }

      return false;
    }
    if (typeof value === "object") {
      if ("hideNulls" in value && "filter" in value) {
        return Object.values(value.filter || {}).filter((v) => v).length > 0 || value.hideNulls;
      }
      if ("hideNulls" in value || "to" in value || "from" in value) {
        return (
          Object.values(value).filter((v) => {
            if (typeof v === "number") {
              return true;
            }
            return v;
          }).length > 0
        );
      }
      if (key === "sort") {
        return false;
      }
      return Object.keys(value).length > 0;
    }
    if (key === "includeHiddenNotices") {
      return value;
    }
    if (typeof value === "string") {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true;
    }
    return false;
  });

  return appliedFilters.map(([key]) => key);
}
